import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Paintbrush, Plus, Trash2, Layers, Info } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import CalculatorSaveBar from '../../components/calculators/CalculatorSaveBar';
import useCalculatorSave from '../../components/calculators/useCalculatorSave';

// ─── Defaults (editable by user) ──────────────────────────────────
// Prices are Quebec contractor avg, Apr 2026. Coverage is per-gallon,
// first coat on primed drywall. Second coat tends to be 20% better,
// but we conservatively apply the same number per coat.

const DEFAULT_PAINT = {
  // Primer (walls + ceilings if new drywall)
  primer_price_per_gal: 35,
  primer_coverage: 300,
  // Walls (acrylic latex, standard quality)
  wall_price_per_gal: 55,
  wall_coverage: 350,
  wall_coats: 2,
  // Ceilings (flat ceiling paint)
  ceiling_price_per_gal: 40,
  ceiling_coverage: 400,
  ceiling_coats: 2,
  // Trim (semi-gloss acrylic)
  trim_price_per_gal: 70,
  trim_coverage: 300,
  trim_coats: 2,
  // Misc
  waste_pct: 10,
};

const PREP_LEVELS = {
  new:   { label: 'New construction', multiplier: 0.7, hint: 'Fresh drywall, minimal patching, no old paint.' },
  good:  { label: 'Good condition',   multiplier: 1.0, hint: 'Light sanding, spot-patch nail holes, minor caulking.' },
  rough: { label: 'Rough / repaint',  multiplier: 1.6, hint: 'Fill cracks, sand glossy surfaces, patch damage, spot-prime.' },
  heavy: { label: 'Heavy repair',     multiplier: 2.3, hint: 'Major wall damage, multiple patches, full prime coat.' },
};

const DEFAULT_LABOUR = {
  prep_hr_per_100sqft: 0.35,         // walls+ceilings combined
  walls_hr_per_100sqft: 0.45,        // per coat
  ceilings_hr_per_100sqft: 0.55,     // per coat (harder overhead)
  trim_hr_per_lf: 0.07,              // per LF of trim, per coat
  cleanup_hr_base: 1.0,              // per job
};

// Rough trim estimation per opening:
//   door:   ~17 lf (casings 2 sides × ~7' + 3' top)
//   window: ~14 lf (casings all around ~3.5' × 4)
// Plus baseboard = perimeter of each room.
const TRIM_LF_PER_DOOR = 17;
const TRIM_LF_PER_WINDOW = 14;
// Trim effective "painted width" — used to convert LF → sqft of trim surface.
// Casings average ~3" wide front face + 1" edges, repeated both sides.
const TRIM_SURFACE_SQFT_PER_LF = 0.65;

// ─── utilities ─────────────────────────────────────────────────────
const fmt = (n, d = 2) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })}`;
const fmtN = (n, d = 2) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const uid = () => Math.random().toString(36).slice(2, 9);

export default function PaintingCalculator() {
  const navigate = useNavigate();

  const [rooms, setRooms] = useState([
    { id: uid(), name: 'Main room', length: 12, width: 10, height: 8, include_ceiling: true, include_baseboard: true },
  ]);
  const [openings, setOpenings] = useState([
    { id: uid(), label: 'Door', width: 3, height: 6.75, count: 1, has_trim: true },
    { id: uid(), label: 'Window', width: 3, height: 4, count: 1, has_trim: true },
  ]);

  const [paintWalls, setPaintWalls] = useState(true);
  const [paintCeilings, setPaintCeilings] = useState(true);
  const [paintTrim, setPaintTrim] = useState(true);
  const [usePrimer, setUsePrimer] = useState(true);

  const [prepLevel, setPrepLevel] = useState('good');
  const [paintSpec, setPaintSpec] = useState(DEFAULT_PAINT);
  const [labourRates, setLabourRates] = useState(DEFAULT_LABOUR);
  const [hourlyRate, setHourlyRate] = useState(65);
  const [markupPct, setMarkup] = useState(20);
  const [notes, setNotes] = useState('');

  // Pull user's default hourly rate
  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${BACKEND_URL}/api/job-costs/settings`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(s => { if (s && s.default_hourly_rate) setHourlyRate(s.default_hourly_rate); })
      .catch(() => {});
  }, []);

  // ─── Save hook ─────────────────────────────────────────────────
  const getConfig = useCallback(() => ({
    rooms, openings, paintWalls, paintCeilings, paintTrim, usePrimer,
    prepLevel, paintSpec, labourRates, hourlyRate, markupPct, notes,
  }), [rooms, openings, paintWalls, paintCeilings, paintTrim, usePrimer,
       prepLevel, paintSpec, labourRates, hourlyRate, markupPct, notes]);
  const applyConfig = useCallback((cfg = {}) => {
    if (cfg.rooms !== undefined) setRooms(cfg.rooms);
    if (cfg.openings !== undefined) setOpenings(cfg.openings);
    if (cfg.paintWalls !== undefined) setPaintWalls(cfg.paintWalls);
    if (cfg.paintCeilings !== undefined) setPaintCeilings(cfg.paintCeilings);
    if (cfg.paintTrim !== undefined) setPaintTrim(cfg.paintTrim);
    if (cfg.usePrimer !== undefined) setUsePrimer(cfg.usePrimer);
    if (cfg.prepLevel !== undefined) setPrepLevel(cfg.prepLevel);
    if (cfg.paintSpec !== undefined) setPaintSpec(cfg.paintSpec);
    if (cfg.labourRates !== undefined) setLabourRates(cfg.labourRates);
    if (cfg.hourlyRate !== undefined) setHourlyRate(cfg.hourlyRate);
    if (cfg.markupPct !== undefined) setMarkup(cfg.markupPct);
    if (cfg.notes !== undefined) setNotes(cfg.notes);
  }, []);

  // ─── Calculations ──────────────────────────────────────────────
  const calc = useMemo(() => {
    // 1. Raw areas
    let grossWallArea = 0;
    let ceilingArea = 0;
    let baseboardLF = 0;
    rooms.forEach(r => {
      grossWallArea += 2 * (Number(r.length) + Number(r.width)) * Number(r.height);
      if (r.include_ceiling) ceilingArea += Number(r.length) * Number(r.width);
      if (r.include_baseboard) baseboardLF += 2 * (Number(r.length) + Number(r.width));
    });

    // 2. Openings: subtract from walls + compute their trim LF
    let openingArea = 0;
    let trimLFFromOpenings = 0;
    openings.forEach(o => {
      const a = Number(o.width) * Number(o.height) * Number(o.count);
      openingArea += a;
      if (o.has_trim) {
        const unitLF = /door/i.test(o.label) ? TRIM_LF_PER_DOOR : TRIM_LF_PER_WINDOW;
        trimLFFromOpenings += unitLF * Number(o.count);
      }
    });
    const wallArea = Math.max(0, grossWallArea - openingArea);

    const trimLF = baseboardLF + trimLFFromOpenings;
    const trimArea = trimLF * TRIM_SURFACE_SQFT_PER_LF;

    const surfaces = {
      walls: paintWalls ? wallArea : 0,
      ceilings: paintCeilings ? ceilingArea : 0,
      trim: paintTrim ? trimArea : 0,
    };
    const totalPaintedSqft = surfaces.walls + surfaces.ceilings + surfaces.trim;

    // 3. Paint gallons per surface (with waste)
    const wastePct = Math.max(0, Number(paintSpec.waste_pct) || 0) / 100;
    const gallons = (sqft, coats, coverage) => {
      if (!sqft || !coats || !coverage) return 0;
      return (sqft * coats * (1 + wastePct)) / coverage;
    };
    const primerSqft = (paintWalls ? wallArea : 0) + (paintCeilings ? ceilingArea : 0);
    const gallonsBreakdown = {
      primer: usePrimer ? gallons(primerSqft, 1, paintSpec.primer_coverage) : 0,
      walls:  paintWalls    ? gallons(surfaces.walls,    paintSpec.wall_coats,    paintSpec.wall_coverage)    : 0,
      ceilings: paintCeilings ? gallons(surfaces.ceilings, paintSpec.ceiling_coats, paintSpec.ceiling_coverage) : 0,
      trim:   paintTrim     ? gallons(surfaces.trim,     paintSpec.trim_coats,    paintSpec.trim_coverage)    : 0,
    };

    // Round up to whole gallons (you don't buy 1.3 gal — you buy 2)
    const gallonsBought = {
      primer: Math.ceil(gallonsBreakdown.primer),
      walls: Math.ceil(gallonsBreakdown.walls),
      ceilings: Math.ceil(gallonsBreakdown.ceilings),
      trim: Math.ceil(gallonsBreakdown.trim),
    };

    const materials = [
      usePrimer && gallonsBought.primer > 0 && {
        label: `Primer (${gallonsBought.primer} gal × ${fmt(paintSpec.primer_price_per_gal)})`,
        cost: gallonsBought.primer * paintSpec.primer_price_per_gal,
      },
      paintWalls && gallonsBought.walls > 0 && {
        label: `Wall paint (${gallonsBought.walls} gal × ${fmt(paintSpec.wall_price_per_gal)}, ${paintSpec.wall_coats} coats)`,
        cost: gallonsBought.walls * paintSpec.wall_price_per_gal,
      },
      paintCeilings && gallonsBought.ceilings > 0 && {
        label: `Ceiling paint (${gallonsBought.ceilings} gal × ${fmt(paintSpec.ceiling_price_per_gal)}, ${paintSpec.ceiling_coats} coats)`,
        cost: gallonsBought.ceilings * paintSpec.ceiling_price_per_gal,
      },
      paintTrim && gallonsBought.trim > 0 && {
        label: `Trim paint (${gallonsBought.trim} gal × ${fmt(paintSpec.trim_price_per_gal)}, ${paintSpec.trim_coats} coats)`,
        cost: gallonsBought.trim * paintSpec.trim_price_per_gal,
      },
    ].filter(Boolean);

    const materialsSubtotal = materials.reduce((s, m) => s + m.cost, 0);

    // 4. Labour (includes prep multiplier)
    const prepMult = PREP_LEVELS[prepLevel]?.multiplier ?? 1;
    const prepHours = prepMult * (primerSqft / 100) * labourRates.prep_hr_per_100sqft;
    const wallHours = paintWalls ? (surfaces.walls / 100) * labourRates.walls_hr_per_100sqft * paintSpec.wall_coats : 0;
    const ceilingHours = paintCeilings ? (surfaces.ceilings / 100) * labourRates.ceilings_hr_per_100sqft * paintSpec.ceiling_coats : 0;
    const trimHours = paintTrim ? trimLF * labourRates.trim_hr_per_lf * paintSpec.trim_coats : 0;
    const cleanupHours = labourRates.cleanup_hr_base;

    const labourBreakdown = [
      { label: `Prep (${PREP_LEVELS[prepLevel]?.label})`, hours: prepHours },
      paintWalls    && { label: `Walls × ${paintSpec.wall_coats} coats`,    hours: wallHours },
      paintCeilings && { label: `Ceilings × ${paintSpec.ceiling_coats} coats`, hours: ceilingHours },
      paintTrim     && { label: `Trim × ${paintSpec.trim_coats} coats`,     hours: trimHours },
      { label: 'Cleanup', hours: cleanupHours },
    ].filter(Boolean);
    const totalHours = labourBreakdown.reduce((s, l) => s + l.hours, 0);
    const labourCost = totalHours * Number(hourlyRate);

    // 5. Markup + totals
    const subtotal = materialsSubtotal + labourCost;
    const totalMarkup = subtotal * (Number(markupPct) || 0) / 100;
    const totalQuote = subtotal + totalMarkup;
    const pricePerSqft = totalPaintedSqft ? totalQuote / totalPaintedSqft : 0;

    return {
      wallArea, ceilingArea, trimArea, trimLF,
      totalPaintedSqft,
      gallonsBreakdown, gallonsBought, materials, materialsSubtotal,
      labourBreakdown, totalHours, labourCost,
      subtotal, totalMarkup, totalQuote, pricePerSqft,
      openingArea, grossWallArea,
    };
  }, [rooms, openings, paintWalls, paintCeilings, paintTrim, usePrimer,
       prepLevel, paintSpec, labourRates, hourlyRate, markupPct]);

  // ─── Room / opening handlers ───────────────────────────────────
  const addRoom = () => setRooms(r => [...r, {
    id: uid(), name: `Room ${r.length + 1}`,
    length: 10, width: 10, height: 8,
    include_ceiling: true, include_baseboard: true,
  }]);
  const updateRoom = (id, patch) => setRooms(r => r.map(x => x.id === id ? { ...x, ...patch } : x));
  const removeRoom = (id) => setRooms(r => r.filter(x => x.id !== id));
  const duplicateRoom = (id) => setRooms(r => {
    const idx = r.findIndex(x => x.id === id);
    if (idx < 0) return r;
    const copy = { ...r[idx], id: uid(), name: `${r[idx].name} (copy)` };
    const next = [...r]; next.splice(idx + 1, 0, copy);
    return next;
  });

  const addOpening = () => setOpenings(o => [...o, {
    id: uid(), label: 'Opening', width: 3, height: 4, count: 1, has_trim: true,
  }]);
  const updateOpening = (id, patch) => setOpenings(o => o.map(x => x.id === id ? { ...x, ...patch } : x));
  const removeOpening = (id) => setOpenings(o => o.filter(x => x.id !== id));

  const updateSpec = (key, value) => setPaintSpec(s => ({ ...s, [key]: value }));
  const updateLabour = (key, value) => setLabourRates(s => ({ ...s, [key]: value }));

  // ─── Build summary + save hook ─────────────────────────────────
  const buildSummary = useCallback(() => {
    const surfaces = [];
    if (paintWalls) surfaces.push(`walls ${fmtN(calc.wallArea, 0)} sqft`);
    if (paintCeilings) surfaces.push(`ceilings ${fmtN(calc.ceilingArea, 0)} sqft`);
    if (paintTrim) surfaces.push(`trim ${fmtN(calc.trimLF, 0)} lf`);
    const text =
`PAINTING — ${rooms.length} room${rooms.length !== 1 ? 's' : ''} · ${PREP_LEVELS[prepLevel].label}
Surfaces: ${surfaces.join(', ') || '(none)'}
Total: ${fmt(calc.totalQuote)} (${fmt(calc.pricePerSqft)}/sqft painted)`;
    return {
      text,
      totals: {
        total_quote: calc.totalQuote,
        area_ft2: calc.totalPaintedSqft,
        price_per_ft2: calc.pricePerSqft,
        labour_cost: calc.labourCost,
        materials_subtotal: calc.materialsSubtotal,
        markup_pct: markupPct,
      },
    };
  }, [rooms, paintWalls, paintCeilings, paintTrim, prepLevel, calc, markupPct]);

  const saveApi = useCalculatorSave({
    calculatorType: 'painting',
    getConfig, applyConfig, getSummary: buildSummary,
  });

  // ─── Copy to clipboard ─────────────────────────────────────────
  const copySummary = async () => {
    const roomsLines = rooms.map(r =>
      `  • ${r.name}: ${r.length}′ × ${r.width}′ × ${r.height}′${r.include_ceiling ? ' (incl. ceiling)' : ''}`
    ).join('\n');

    const matLines = calc.materials.map(m => `  ${m.label}: ${fmt(m.cost)}`).join('\n') || '  —';
    const labLines = calc.labourBreakdown.map(l => `  ${l.label}: ${l.hours.toFixed(1)} hrs`).join('\n');

    const surfaces = [];
    if (paintWalls) surfaces.push(`Walls: ${fmtN(calc.wallArea, 0)} sqft`);
    if (paintCeilings) surfaces.push(`Ceilings: ${fmtN(calc.ceilingArea, 0)} sqft`);
    if (paintTrim) surfaces.push(`Trim: ${fmtN(calc.trimLF, 0)} lf (~${fmtN(calc.trimArea, 0)} sqft)`);

    const text =
`PAINTING — ${rooms.length} room${rooms.length !== 1 ? 's' : ''}

Prep: ${PREP_LEVELS[prepLevel].label} · Primer: ${usePrimer ? 'yes' : 'no'}

Rooms:
${roomsLines}

Surfaces:
  ${surfaces.join('\n  ')}

Materials:
${matLines}
  Subtotal: ${fmt(calc.materialsSubtotal)}

Labour:
${labLines}
  Total: ${calc.totalHours.toFixed(1)} hrs × ${fmt(hourlyRate)} = ${fmt(calc.labourCost)}

Markup (${markupPct}%): ${fmt(calc.totalMarkup)}
TOTAL: ${fmt(calc.totalQuote)} (${fmt(calc.pricePerSqft)}/sqft painted)
${notes ? `\nNotes: ${notes}` : ''}`;
    try {
      await navigator.clipboard.writeText(text.trim());
      toast.success('Painting summary copied to clipboard');
    } catch { toast.error('Clipboard unavailable'); }
  };

  // ─── Render ────────────────────────────────────────────────────
  return (
    <div className="animate-fade-in" data-testid="painting-calculator">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/price-book')}
            className="p-2 rounded-md text-[#6B7A73] hover:text-[#1A1D1C] hover:bg-[#F0F3F1] transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#E5F0EE] flex items-center justify-center">
              <Paintbrush className="w-5 h-5 text-[#2D6B68]" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
                Painting Calculator
              </h1>
              <p className="text-xs text-[#6B7A73] mt-0.5">
                Rooms × surfaces × prep level → primer, paint, labour, markup
              </p>
            </div>
          </div>
        </div>
        <Button
          onClick={copySummary}
          className="bg-[#2D6B68] text-white hover:bg-[#235552] rounded-md"
          data-testid="copy-summary-btn"
        >
          <Copy className="w-4 h-4 mr-2" />
          Copy Summary
        </Button>
      </div>

      <CalculatorSaveBar {...saveApi} />

      {/* Surface toggles */}
      <div className="bg-white border border-[#E6EBE8] rounded-md p-4 mb-5">
        <div className="flex flex-wrap items-center gap-4">
          <h2 className="text-xs font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">Paint these surfaces</h2>
          <ToggleChip label="Walls" active={paintWalls} onClick={() => setPaintWalls(v => !v)} testid="toggle-walls" />
          <ToggleChip label="Ceilings" active={paintCeilings} onClick={() => setPaintCeilings(v => !v)} testid="toggle-ceilings" />
          <ToggleChip label="Trim" active={paintTrim} onClick={() => setPaintTrim(v => !v)} testid="toggle-trim" />
          <ToggleChip label="Primer" active={usePrimer} onClick={() => setUsePrimer(v => !v)} testid="toggle-primer" />
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-[#6B7A73]">Prep</span>
            <Select value={prepLevel} onValueChange={setPrepLevel}>
              <SelectTrigger className="w-44 h-8 text-sm bg-white border-[#E6EBE8]" data-testid="prep-level">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PREP_LEVELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-[11px] text-[#6B7A73] mt-2">{PREP_LEVELS[prepLevel].hint}</p>
      </div>

      {/* ─── Two-column layout ─────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: INPUTS */}
        <div className="space-y-6">
          {/* Rooms */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5" data-testid="rooms-panel">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">Rooms</h2>
              <Button onClick={addRoom} variant="ghost" size="sm" className="text-[#2D6B68] hover:bg-[#F0F3F1]" data-testid="add-room-btn">
                <Plus className="w-4 h-4 mr-1" /> Add room
              </Button>
            </div>
            <div className="space-y-3">
              {rooms.map((r, idx) => (
                <div key={r.id} className="border border-[#E6EBE8] rounded-md p-3 bg-[#F9FAFA]" data-testid={`room-${idx}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Input value={r.name} onChange={e => updateRoom(r.id, { name: e.target.value })}
                      className="flex-1 h-8 text-sm border-[#E6EBE8] bg-white" placeholder="Room name"
                      data-testid={`room-name-${idx}`} />
                    <button onClick={() => duplicateRoom(r.id)} className="p-1.5 text-[#6B7A73] hover:text-[#2D6B68] hover:bg-white rounded" title="Duplicate">
                      <Layers className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => removeRoom(r.id)} disabled={rooms.length <= 1}
                      className="p-1.5 text-[#6B7A73] hover:text-red-600 hover:bg-white rounded disabled:opacity-30" title="Remove">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <DimInput label="Length (ft)" value={r.length} onChange={v => updateRoom(r.id, { length: v })} testid={`room-length-${idx}`} />
                    <DimInput label="Width (ft)" value={r.width} onChange={v => updateRoom(r.id, { width: v })} testid={`room-width-${idx}`} />
                    <DimInput label="Height (ft)" value={r.height} onChange={v => updateRoom(r.id, { height: v })} testid={`room-height-${idx}`} />
                  </div>
                  <div className="flex items-center gap-4 flex-wrap">
                    <label className="flex items-center gap-2 text-xs text-[#3A423E] cursor-pointer">
                      <Checkbox checked={r.include_ceiling} onCheckedChange={v => updateRoom(r.id, { include_ceiling: !!v })} />
                      Ceiling
                    </label>
                    <label className="flex items-center gap-2 text-xs text-[#3A423E] cursor-pointer">
                      <Checkbox checked={r.include_baseboard} onCheckedChange={v => updateRoom(r.id, { include_baseboard: !!v })} />
                      Baseboard
                    </label>
                    <span className="ml-auto text-[10px] text-[#6B7A73]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      walls {fmtN(2 * (Number(r.length) + Number(r.width)) * Number(r.height), 0)} sqft
                      {r.include_ceiling ? ` · ceil ${fmtN(Number(r.length) * Number(r.width), 0)} sqft` : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Openings */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5" data-testid="openings-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">Openings</h2>
                <p className="text-[11px] text-[#6B7A73] mt-0.5">Subtracted from wall area. Check "Trim" to include their casings.</p>
              </div>
              <Button onClick={addOpening} variant="ghost" size="sm" className="text-[#2D6B68] hover:bg-[#F0F3F1]">
                <Plus className="w-4 h-4 mr-1" /> Add opening
              </Button>
            </div>
            <div className="space-y-2">
              {openings.map((o, idx) => (
                <div key={o.id} className="grid grid-cols-[1fr_80px_80px_70px_auto_30px] gap-2 items-center" data-testid={`opening-${idx}`}>
                  <Input value={o.label} onChange={e => updateOpening(o.id, { label: e.target.value })}
                    className="h-8 text-sm border-[#E6EBE8] bg-white" placeholder="Door, Window…" />
                  <DimInput value={o.width} onChange={v => updateOpening(o.id, { width: v })} placeholder="W" />
                  <DimInput value={o.height} onChange={v => updateOpening(o.id, { height: v })} placeholder="H" />
                  <DimInput value={o.count} onChange={v => updateOpening(o.id, { count: v })} placeholder="×" step={1} />
                  <label className="flex items-center gap-1 text-[11px] text-[#3A423E] cursor-pointer">
                    <Checkbox checked={o.has_trim} onCheckedChange={v => updateOpening(o.id, { has_trim: !!v })} />
                    Trim
                  </label>
                  <button onClick={() => removeOpening(o.id)} className="p-1.5 text-[#6B7A73] hover:text-red-600 hover:bg-[#F0F3F1] rounded">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {openings.length === 0 && (
                <p className="text-xs text-[#6B7A73] italic">No openings — nothing subtracted from walls.</p>
              )}
            </div>
          </div>

          {/* Pricing parameters */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5">
            <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-4">Labour & markup</h2>
            <div className="space-y-4">
              <LabelledSlider label="Hourly rate ($)" min={25} max={200} step={5}
                value={hourlyRate} onChange={setHourlyRate} testid="hourly-rate" />
              <LabelledSlider label="Markup (%)" min={0} max={50} step={1}
                value={markupPct} onChange={setMarkup} testid="markup" />
            </div>
          </div>

          {/* Paint prices */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5">
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-3.5 h-3.5 text-[#6B7A73]" />
              <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">Paint prices & coverage</h2>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {usePrimer && (
                <>
                  <PriceField label="Primer / gal" value={paintSpec.primer_price_per_gal} onChange={v => updateSpec('primer_price_per_gal', v)} />
                  <PriceField label="Primer coverage (sqft/gal)" value={paintSpec.primer_coverage} onChange={v => updateSpec('primer_coverage', v)} nodollar />
                </>
              )}
              {paintWalls && (
                <>
                  <PriceField label="Wall paint / gal" value={paintSpec.wall_price_per_gal} onChange={v => updateSpec('wall_price_per_gal', v)} />
                  <PriceField label="Wall coverage (sqft/gal)" value={paintSpec.wall_coverage} onChange={v => updateSpec('wall_coverage', v)} nodollar />
                  <CoatsField label="Wall coats" value={paintSpec.wall_coats} onChange={v => updateSpec('wall_coats', v)} />
                </>
              )}
              {paintCeilings && (
                <>
                  <PriceField label="Ceiling paint / gal" value={paintSpec.ceiling_price_per_gal} onChange={v => updateSpec('ceiling_price_per_gal', v)} />
                  <PriceField label="Ceiling coverage" value={paintSpec.ceiling_coverage} onChange={v => updateSpec('ceiling_coverage', v)} nodollar />
                  <CoatsField label="Ceiling coats" value={paintSpec.ceiling_coats} onChange={v => updateSpec('ceiling_coats', v)} />
                </>
              )}
              {paintTrim && (
                <>
                  <PriceField label="Trim paint / gal" value={paintSpec.trim_price_per_gal} onChange={v => updateSpec('trim_price_per_gal', v)} />
                  <PriceField label="Trim coverage" value={paintSpec.trim_coverage} onChange={v => updateSpec('trim_coverage', v)} nodollar />
                  <CoatsField label="Trim coats" value={paintSpec.trim_coats} onChange={v => updateSpec('trim_coats', v)} />
                </>
              )}
              <PriceField label="Waste %" value={paintSpec.waste_pct} onChange={v => updateSpec('waste_pct', v)} nodollar />
            </div>
            <div className="mt-4 pt-4 border-t border-[#E6EBE8]">
              <p className="text-xs text-[#6B7A73] mb-2">Labour rates (hrs / 100 sqft or LF):</p>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <PriceField label="Prep / 100sqft" value={labourRates.prep_hr_per_100sqft} onChange={v => updateLabour('prep_hr_per_100sqft', v)} nodollar />
                <PriceField label="Walls / 100sqft" value={labourRates.walls_hr_per_100sqft} onChange={v => updateLabour('walls_hr_per_100sqft', v)} nodollar />
                <PriceField label="Ceilings / 100sqft" value={labourRates.ceilings_hr_per_100sqft} onChange={v => updateLabour('ceilings_hr_per_100sqft', v)} nodollar />
                <PriceField label="Trim / LF" value={labourRates.trim_hr_per_lf} onChange={v => updateLabour('trim_hr_per_lf', v)} nodollar />
                <PriceField label="Cleanup (hrs)" value={labourRates.cleanup_hr_base} onChange={v => updateLabour('cleanup_hr_base', v)} nodollar />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5">
            <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-3">Notes</h2>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              className="w-full bg-white border border-[#E6EBE8] rounded-md p-2.5 text-sm focus:border-[#2D6B68] focus:outline-none"
              placeholder="Paint brand, colour, finish (matte/eggshell/semi-gloss), special prep, etc."
              data-testid="notes"
            />
          </div>
        </div>

        {/* RIGHT: OUTPUTS */}
        <div className="space-y-6">
          {/* Areas */}
          <div className="grid grid-cols-3 gap-3">
            <SummaryCard label="Walls" value={fmtN(calc.wallArea, 0)} unit="ft²" subtle={!paintWalls} />
            <SummaryCard label="Ceilings" value={fmtN(calc.ceilingArea, 0)} unit="ft²" subtle={!paintCeilings} />
            <SummaryCard label="Trim" value={fmtN(calc.trimLF, 0)} unit="lf" subtle={!paintTrim} />
          </div>

          {/* Gallons */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5">
            <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-3">Paint needed</h2>
            <div className="space-y-1.5 text-sm">
              {usePrimer && calc.gallonsBought.primer > 0 && (
                <GallonRow label="Primer" calc={calc.gallonsBreakdown.primer} bought={calc.gallonsBought.primer} />
              )}
              {paintWalls && calc.gallonsBought.walls > 0 && (
                <GallonRow label={`Walls × ${paintSpec.wall_coats} coats`} calc={calc.gallonsBreakdown.walls} bought={calc.gallonsBought.walls} />
              )}
              {paintCeilings && calc.gallonsBought.ceilings > 0 && (
                <GallonRow label={`Ceilings × ${paintSpec.ceiling_coats} coats`} calc={calc.gallonsBreakdown.ceilings} bought={calc.gallonsBought.ceilings} />
              )}
              {paintTrim && calc.gallonsBought.trim > 0 && (
                <GallonRow label={`Trim × ${paintSpec.trim_coats} coats`} calc={calc.gallonsBreakdown.trim} bought={calc.gallonsBought.trim} />
              )}
              {!usePrimer && !paintWalls && !paintCeilings && !paintTrim && (
                <p className="text-[11px] text-[#6B7A73] italic">No surfaces selected.</p>
              )}
            </div>
          </div>

          {/* Materials */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5">
            <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-3">Materials (before markup)</h2>
            <div className="space-y-1.5 text-sm">
              {calc.materials.map((m, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-[#3A423E]">{m.label}</span>
                  <span className="text-[#1A1D1C]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(m.cost)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-2 border-t border-[#E6EBE8] font-semibold">
                <span>Subtotal</span>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(calc.materialsSubtotal)}</span>
              </div>
            </div>
          </div>

          {/* Labour */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5">
            <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-3">Labour</h2>
            <div className="space-y-1.5 text-sm">
              {calc.labourBreakdown.map((l, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-[#3A423E]">{l.label}</span>
                  <span className="text-[#1A1D1C]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{l.hours.toFixed(1)} hrs</span>
                </div>
              ))}
              <div className="flex justify-between pt-2 border-t border-[#E6EBE8] font-semibold">
                <span>Total</span>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{calc.totalHours.toFixed(1)} hrs</span>
              </div>
              <div className="flex justify-between text-[#6B7A73]">
                <span>@ {fmt(hourlyRate)}/hr</span>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(calc.labourCost)}</span>
              </div>
            </div>
          </div>

          {/* Final */}
          <div className="bg-[#1A1D1C] text-white rounded-md p-5">
            <div className="flex justify-between items-baseline mb-1">
              <span className="text-xs uppercase tracking-[0.15em] text-white/60">Subtotal</span>
              <span className="text-lg" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(calc.subtotal)}</span>
            </div>
            <div className="flex justify-between items-baseline mb-3">
              <span className="text-xs uppercase tracking-[0.15em] text-white/60">Markup ({markupPct}%)</span>
              <span className="text-lg" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(calc.totalMarkup)}</span>
            </div>
            <div className="flex justify-between items-baseline pt-3 border-t border-white/20">
              <span className="text-sm uppercase tracking-[0.15em]">Quote</span>
              <span className="text-3xl font-semibold" style={{ fontFamily: "'IBM Plex Mono', monospace" }} data-testid="total-quote">
                {fmt(calc.totalQuote)}
              </span>
            </div>
            <p className="text-xs text-white/60 mt-1 text-right">
              {fmt(calc.pricePerSqft)} / sqft painted
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────
function ToggleChip({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
        active
          ? 'bg-[#2D6B68] text-white border-[#2D6B68]'
          : 'bg-white text-[#6B7A73] border-[#E6EBE8] hover:border-[#2D6B68]'
      }`}
    >
      {label}
    </button>
  );
}

function DimInput({ label, value, onChange, placeholder, step = 0.5, testid }) {
  return (
    <div>
      {label && <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">{label}</label>}
      <Input
        type="number"
        step={step}
        value={value}
        onChange={e => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
        className="h-8 text-sm border-[#E6EBE8] bg-white"
        placeholder={placeholder}
        data-testid={testid}
      />
    </div>
  );
}

function LabelledSlider({ label, min, max, step, value, onChange, testid }) {
  return (
    <div>
      <div className="flex justify-between mb-1.5">
        <label className="text-xs text-[#3A423E] font-medium">{label}</label>
        <span className="text-xs text-[#1A1D1C]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{value}</span>
      </div>
      <Slider value={[value]} min={min} max={max} step={step}
        onValueChange={v => onChange(v[0])} data-testid={testid} />
    </div>
  );
}

function PriceField({ label, value, onChange, nodollar }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">{label}</label>
      <div className="relative">
        {!nodollar && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-[#6B7A73]">$</span>}
        <Input
          type="number"
          step={nodollar ? 0.01 : 1}
          value={value}
          onChange={e => onChange(Number(e.target.value) || 0)}
          className={`h-8 text-sm border-[#E6EBE8] bg-white ${nodollar ? '' : 'pl-6'}`}
        />
      </div>
    </div>
  );
}

function CoatsField({ label, value, onChange }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">{label}</label>
      <Select value={String(value)} onValueChange={v => onChange(Number(v))}>
        <SelectTrigger className="h-8 text-sm bg-white border-[#E6EBE8]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {[1, 2, 3, 4].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function SummaryCard({ label, value, unit, subtle }) {
  return (
    <div className={`bg-white border border-[#E6EBE8] rounded-md p-4 text-center ${subtle ? 'opacity-50' : ''}`}>
      <p className="text-[10px] uppercase tracking-[0.15em] text-[#6B7A73] font-semibold">{label}</p>
      <p className="text-2xl font-semibold text-[#1A1D1C] mt-1" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
        {value} <span className="text-sm text-[#6B7A73] font-normal">{unit}</span>
      </p>
    </div>
  );
}

function GallonRow({ label, calc, bought }) {
  return (
    <div className="flex justify-between">
      <span className="text-[#3A423E]">{label}</span>
      <span className="text-[#1A1D1C]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
        {calc.toFixed(2)} <span className="text-[#6B7A73]">→ buy {bought} gal</span>
      </span>
    </div>
  );
}
